import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Code2,
  Filter,
  Gift,
  Handshake,
  Hash,
  KeyRound,
  Languages,
  Layers,
  LayoutTemplate,
  Link2,
  Mail,
  Radio,
  Scale,
  ScrollText,
  Settings2,
  Shield,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react'

import { AppShell } from '@/admin/components/app-shell'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

type SidebarItem = {
  label: string
  path: string
  icon: LucideIcon
  description: string
}

type SidebarGroup = {
  title: string
  items: SidebarItem[]
}

export function SettingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const integrationsEnabled = useFeatureFlag(FLAGS.INTEGRATIONS)
  const rewardsEnabled = useFeatureFlag(FLAGS.REWARDS)
  const exceptionsEnabled = useFeatureFlag(FLAGS.EXCEPTIONS)
  const emailAuthoringEnabled = useFeatureFlag(FLAGS.EMAIL_AUTHORING)
  const emailTriggersEnabled = useFeatureFlag(FLAGS.EMAIL_TRIGGERS)
  const communicationsEnabled = useFeatureFlag(FLAGS.COMMUNICATIONS)
  const claimsEnabled = useFeatureFlag(FLAGS.CLAIMS)
  const inspectionsEnabled = useFeatureFlag(FLAGS.INSPECTIONS)
  const identifiersEnabled = useFeatureFlag(FLAGS.IDENTIFIERS)
  const approvalsEnabled = useFeatureFlag(FLAGS.APPROVALS)
  const publicApiEnabled = useFeatureFlag(FLAGS.PUBLIC_API)
  // Each Pulse product (webhooks, email triggers, workflow actions)
  // gets its own settings entry once it ships a UI. Today only
  // Webhooks does.
  const webhooksEnabled = useFeatureFlag(FLAGS.PULSE_WEBHOOKS)
  const { permissions, can, isAdmin } = usePermissions()
  const canOpenExceptions =
    permissions.has('exceptions.view') || permissions.has('exceptions.manage')
  // Rewards move money - the menu entries (and their routes) are gated by
  // rewards.view on top of the rewards flag, so only admins see them today.
  const canViewRewards = can('rewards.view')
  // Internal-only: partners hold claims.view.acl on their own claims, so the
  // flag alone would hand them the internal correspondence about them.
  const canViewCommsActivity = can('communications.view_activity')
  // Forms reuse the email-template permissions; no flag (platform feature).
  const canManageForms =
    can('templates.view.global') ||
    can('templates.view.acl') ||
    can('templates.edit.global')

  const groups: SidebarGroup[] = [
    {
      title: 'Business',
      items: [
        {
          label: 'Organisation',
          path: '/settings/organisation',
          icon: Building2,
          description: 'Name, logo & branding',
        },
        // Admin/superadmin-only: the consumer welcome (landing) page editor.
        // The route is `<ProtectedRoute adminOnly>` and the backend enforces
        // `IsOrgAdmin`; this hides the entry for non-admins who can open
        // Settings (e.g. a role holding `settings.view`).
        ...(isAdmin
          ? [
              {
                label: t('settings.welcomePage', { defaultValue: 'Welcome Page' }),
                path: '/settings/welcome-page',
                icon: LayoutTemplate,
                description: t('settings.welcomePageDescription', {
                  defaultValue: 'Consumer landing page',
                }),
              },
            ]
          : []),
        {
          label: 'Platform',
          path: '/settings/platform',
          icon: Settings2,
          description: 'Dates, currency & region',
        },
        {
          label: t('settings.users', { defaultValue: 'Users' }),
          path: '/settings/users',
          icon: Users,
          description: 'Invite & manage teammates',
        },
        // The roles API needs roles.view (settings.view alone is refused).
        ...(can('roles.view')
          ? [
              {
                label: t('settings.roles', { defaultValue: 'Roles' }),
                path: '/settings/roles',
                icon: Shield,
                description: 'Permissions & access levels',
              },
            ]
          : []),
        {
          label: t('settings.mfa', { defaultValue: 'Security' }),
          path: '/settings/mfa',
          icon: ShieldCheck,
          description: 'MFA & login policies',
        },
      ],
    },
    {
      title: 'Configuration',
      items: [
        {
          label: 'Registration',
          path: '/settings/registration',
          icon: ClipboardList,
          description: 'Product registration flow',
        },
        ...(can('registration-settings.manage')
          ? [
              {
                label: t('consent.title', { defaultValue: 'Consent' }),
                path: '/settings/consent',
                icon: Handshake,
                description: t('settings.consentDescription', {
                  defaultValue: 'Terms & marketing opt-in',
                }),
              },
            ]
          : []),
        ...(claimsEnabled
          ? [
              {
                label: 'Claims',
                path: '/settings/claims',
                icon: ClipboardCheck,
                description: approvalsEnabled
                  ? 'Field visibility & approval policy'
                  : 'Claim field visibility rules',
              },
            ]
          : []),
        ...(inspectionsEnabled &&
        (can('inspection_subtype.create') || can('inspection_subtype.edit'))
          ? [
              {
                label: t('settings.inspectionTypes', {
                  defaultValue: 'Inspection types',
                }),
                path: '/settings/inspection-types',
                icon: Layers,
                description: t('settings.inspectionTypesDescription', {
                  defaultValue: 'Names, links and order',
                }),
              },
            ]
          : []),
        ...(identifiersEnabled
          ? [
              {
                label: t('identifiers.title', { defaultValue: 'Identifiers' }),
                path: '/settings/identifiers',
                icon: Hash,
                description: t('identifiers.description', {
                  defaultValue: 'Readable registration & claim numbers',
                }),
              },
            ]
          : []),
        ...(exceptionsEnabled && canOpenExceptions
          ? [
              {
                label: 'Exceptions',
                path: '/settings/exceptions',
                icon: Scale,
                description: 'Out-of-policy rules',
              },
            ]
          : []),
        {
          label: t('settings.translations', { defaultValue: 'Translations' }),
          path: '/settings/translations',
          icon: Languages,
          description: 'Labels & display text',
        },
        ...(integrationsEnabled
          ? [
              {
                label: 'Integrations',
                path: '/settings/integrations',
                icon: Link2,
                description: 'Connected services',
              },
            ]
          : []),
        ...(rewardsEnabled && canViewRewards
          ? [
              {
                label: 'Rewards',
                path: '/settings/rewards',
                icon: Gift,
                description: 'When claims issue rewards',
              },
              {
                label: 'Rewards activity',
                path: '/settings/rewards-activity',
                icon: Gift,
                description: 'Issued rewards & delivery status',
              },
            ]
          : []),
      ],
    },
    {
      title: 'Messaging',
      items: [
        ...(emailAuthoringEnabled
          ? [
              {
                label: 'Email Templates',
                path: '/settings/email-templates',
                icon: Mail,
                description: 'Customer email designs',
              },
            ]
          : []),
        ...(canManageForms
          ? [
              {
                label: t('settings.forms', { defaultValue: 'Forms' }),
                path: '/settings/forms',
                icon: ClipboardList,
                description: t('settings.formsDescription', {
                  defaultValue: 'Questions sent by link',
                }),
              },
            ]
          : []),
        ...(emailTriggersEnabled
          ? [
              {
                label: 'Email Triggers',
                path: '/settings/email-triggers',
                icon: Filter,
                description: 'Automated email rules',
              },
            ]
          : []),
        {
          label: t('settings.reminders', { defaultValue: 'Reminders' }),
          path: '/settings/reminders',
          icon: CalendarClock,
          description: t('settings.remindersDescription', {
            defaultValue: 'Follow up on missing documents',
          }),
        },
        // The log carries internal correspondence, so the permission gates it
        // on top of the flag - a flag-only gate would show it to every user.
        ...(communicationsEnabled && canViewCommsActivity
          ? [
              {
                label: 'Activity',
                path: '/settings/communications',
                icon: ScrollText,
                description: 'Every message sent, and not sent',
              },
            ]
          : []),
      ],
    },
    {
      title: 'Developer',
      items: [
        {
          label: 'API Keys',
          path: '/settings/api-keys',
          icon: KeyRound,
          description: 'Bearer tokens & access',
        },
        {
          label: 'API Logs',
          path: '/settings/api-logs',
          icon: ScrollText,
          description: 'Request history',
        },
        ...(webhooksEnabled
          ? [
              {
                label: 'Webhooks',
                path: '/settings/webhooks',
                icon: Radio,
                description: 'Outbound event delivery',
              },
            ]
          : []),
        ...(publicApiEnabled
          ? [
              {
                label: 'Developer Portal',
                path: '/admin/developers',
                icon: Code2,
                description: 'API docs, guides & spec',
              },
            ]
          : []),
      ],
    },
  ].filter((g) => g.items.length > 0)

  /* ── Responsive sidebar toggle ── */

  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 1024px)').matches
      : false,
  )
  const [showSidebar, setShowSidebar] = useState(() => {
    if (typeof window === 'undefined') return true
    const isMobile = !window.matchMedia('(min-width: 1024px)').matches
    return isMobile
      ? location.pathname === '/settings' || location.pathname === '/settings/'
      : true
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches)
      if (e.matches) setShowSidebar(true)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const handleNavClick = (path: string) => {
    navigate(path)
    if (!isDesktop) setShowSidebar(false)
  }

  /* ── Sidebar ── */

  const sidebar = (
    <nav className="flex flex-col gap-6">
      {groups.map((group) => (
        <div key={group.title}>
          <span className="mb-2 block px-3 text-[9px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
            {group.title}
          </span>
          <div className="flex flex-col gap-px">
            {group.items.map((item) => {
              const isActive = location.pathname.startsWith(item.path)
              const Icon = item.icon
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavClick(item.path)}
                  className={cn(
                    'group flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left transition-all duration-150',
                    isActive
                      ? 'bg-[var(--bg)] shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
                      : 'bg-transparent hover:bg-[var(--bg)]',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-150',
                      isActive
                        ? 'bg-[var(--green-bg)] text-[var(--green)]'
                        : 'bg-[var(--bg-subtle)] text-[var(--ink-3)] group-hover:bg-[var(--green-bg)] group-hover:text-[var(--green)]',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <span
                      className={cn(
                        'block text-[12px] transition-colors duration-150',
                        isActive
                          ? 'font-normal text-[var(--ink)]'
                          : 'font-light text-[var(--ink)] group-hover:text-[var(--ink)]',
                      )}
                    >
                      {item.label}
                    </span>
                    <span className="block text-[10px] font-light leading-tight text-[var(--ink-3)]">
                      {item.description}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </nav>
  )

  return (
    <AppShell>
      <main className="page-container py-10">
        <h1 className="clara-title mb-6">{t('settings.title')}</h1>

        {/* Desktop: sidebar + content. Fixed height (not min-height) so the
            content pane scrolls INTERNALLY instead of growing the page and
            scrolling the window - that internal scroll is what lets a tab's
            `sticky top-0` action bar (e.g. the email-template editor's Save)
            actually pin while the form scrolls. */}
        <div className="hidden h-[calc(100vh-200px)] min-h-[500px] overflow-hidden rounded-lg border-[0.5px] border-[var(--rule)] lg:flex">
          <div className="w-[240px] shrink-0 overflow-y-auto border-r-[0.5px] border-r-[var(--rule)] bg-[var(--bg-subtle)] px-2 py-4">
            {sidebar}
          </div>
          <div className="flex-1 overflow-y-auto bg-[var(--bg-light)] px-8 py-6">
            <Outlet />
          </div>
        </div>

        {/* Mobile: toggle sidebar / content */}
        <div className="min-h-[calc(100vh-200px)] overflow-hidden rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] lg:hidden">
          {showSidebar ? (
            <div className="bg-[var(--bg-subtle)] p-3">{sidebar}</div>
          ) : (
            <div className="p-4">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowSidebar(true)}
                className="mb-4 gap-1 text-xs text-[var(--ink-3)]"
              >
                <ArrowLeft className="size-3" />
                Back to settings
              </Button>
              <Outlet />
            </div>
          )}
        </div>
      </main>
    </AppShell>
  )
}
